import type { ReactNode } from 'react';
import { Tooltip, type DataTableColumn } from '@odo/shared-ui';
import type {
    OrthodontistListItem,
    OrthodontistsSortBy
} from '../../../services/orthodontistAccounts';
import { formatCreatedDate } from '../../../utils/dates';

const ORTHODONTISTS_TD_PADDING = 'px-3 first:pl-5 last:pr-4 overflow-hidden';
const ORTHODONTISTS_TH_PADDING = '!px-3 first:!pl-5 last:!pr-4';

// Plain functions, not components - same convention as practiceColumns.tsx's
// practiceCell (a local component here would trip react-refresh's boundary rule).
function orthodontistCell(orthodontist: OrthodontistListItem): ReactNode {
    const doctorLine = `Dr. ${orthodontist.drFirstName} ${orthodontist.drLastName}`.trim();
    return (
        <div className="flex flex-col min-w-0">
            <Tooltip content={orthodontist.name}>
                <span className="font-semibold text-gray-900 truncate min-w-0">
                    {orthodontist.name}
                </span>
            </Tooltip>
            <Tooltip content={doctorLine}>
                <span className="block text-[12px] text-gray-500 truncate">{doctorLine}</span>
            </Tooltip>
        </div>
    );
}

export function buildOrthodontistColumns(): DataTableColumn<
    OrthodontistListItem,
    OrthodontistsSortBy
>[] {
    return [
        {
            key: 'orthodontist',
            label: 'Orthodontist',
            width: '34%',
            sortable: true,
            sortKey: 'name',
            cellClassName: ORTHODONTISTS_TD_PADDING,
            headerClassName: ORTHODONTISTS_TH_PADDING,
            render: orthodontistCell
        },
        {
            key: 'location',
            label: 'Location',
            width: '20%',
            cellClassName: `${ORTHODONTISTS_TD_PADDING} text-sm text-gray-600 whitespace-nowrap`,
            headerClassName: `${ORTHODONTISTS_TH_PADDING} whitespace-nowrap`,
            render: (orthodontist) =>
                orthodontist.city && orthodontist.state ? (
                    `${orthodontist.city}, ${orthodontist.state}`
                ) : (
                    <span className="text-gray-300">-</span>
                )
        },
        {
            key: 'patients',
            label: 'Patients',
            width: '15%',
            sortable: true,
            sortKey: 'patientCount',
            cellClassName: `${ORTHODONTISTS_TD_PADDING} tabular-nums font-medium text-gray-700 whitespace-nowrap`,
            headerClassName: `${ORTHODONTISTS_TH_PADDING} whitespace-nowrap`,
            render: (orthodontist) =>
                orthodontist.patientCount > 0 ? (
                    orthodontist.patientCount
                ) : (
                    <span className="text-gray-300">0</span>
                )
        },
        {
            key: 'practices',
            label: 'Practices',
            width: '15%',
            sortable: true,
            sortKey: 'practiceCount',
            cellClassName: `${ORTHODONTISTS_TD_PADDING} tabular-nums font-medium text-gray-700 whitespace-nowrap`,
            headerClassName: `${ORTHODONTISTS_TH_PADDING} whitespace-nowrap`,
            render: (orthodontist) =>
                orthodontist.practiceCount > 0 ? (
                    orthodontist.practiceCount
                ) : (
                    <span className="text-gray-300">0</span>
                )
        },
        {
            key: 'added',
            label: 'Added',
            width: '16%',
            sortable: true,
            sortKey: 'createdAt',
            cellClassName: `${ORTHODONTISTS_TD_PADDING} text-sm text-gray-500 whitespace-nowrap`,
            headerClassName: ORTHODONTISTS_TH_PADDING,
            render: (orthodontist) => formatCreatedDate(orthodontist.createdAt)
        }
    ];
}
